import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  LocalAtm as MoneyIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  FlashOn as QuickStartIcon
} from '@mui/icons-material';
import calculatorApi from '../services/calculatorApi';
import api from '../axiosConfig';

// QS categories that must always be excluded from the full overhead calc
const QS_CATEGORIES = ['QS Rent', 'QS Utilities', 'QS Insurance', 'QS Other'];

const EMPTY_QS = { rent: '', utilities: '', insurance: '', other: '' };

const CalculatorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    totalServices: 0,
    totalCategories: 0,
    averagePrice: 0,
    calculatedServices: 0,
    pendingServices: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);

  // Quick Start dialog state
  const [qsOpen, setQsOpen] = useState(false);
  const [qsValues, setQsValues] = useState(EMPTY_QS);
  const [qsSubmitting, setQsSubmitting] = useState(false);
  const [qsError, setQsError] = useState(null);
  const [qsSuccess, setQsSuccess] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [services, categories, settings] = await Promise.all([
        calculatorApi.getServices(),
        calculatorApi.getServiceCategories(),
        calculatorApi.getBusinessSettings()
      ]);

      const calculatedCount = services.filter(s => s.last_calculated_at !== null).length;

      // Average of every tier price that has actually been set, across all
      // services. (This used to average the old default price.)
      const allTierPrices = services
        .flatMap(s => s.tier_prices || [])
        .map(p => parseFloat(p.price))
        .filter(p => p > 0);
      const avgPrice = allTierPrices.length > 0
        ? allTierPrices.reduce((sum, p) => sum + p, 0) / allTierPrices.length
        : 0;

      setStats({
        totalServices: services.length,
        totalCategories: categories.length,
        averagePrice: avgPrice,
        calculatedServices: calculatedCount,
        pendingServices: services.length - calculatedCount
      });

      setBusinessSettings(settings);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isColdStart = !businessSettings ||
    businessSettings.overhead_per_minute === null ||
    businessSettings.overhead_per_minute === undefined ||
    parseFloat(businessSettings.overhead_per_minute) === 0;

  // ── Quick Start submission ──────────────────────────────────────────────────
  // Each monthly figure is multiplied by 12 to produce an annual expenditure
  // record. Records are created against a "Quick Start" supplier using the
  // QS_* categories so they can be identified and removed later when the
  // account has real expenditure data.
  const handleQsSubmit = async () => {
    const hasAny = Object.values(qsValues).some(v => v && parseFloat(v) > 0);
    if (!hasAny) {
      setQsError('Please enter at least one monthly cost.');
      return;
    }

    setQsSubmitting(true);
    setQsError(null);

    try {
      await api.post('/calculator/quick-overhead', {
        rent:      parseFloat(qsValues.rent)      || 0,
        utilities: parseFloat(qsValues.utilities)  || 0,
        insurance: parseFloat(qsValues.insurance)  || 0,
        other:     parseFloat(qsValues.other)      || 0,
      });

      setQsSuccess(true);
      setQsValues(EMPTY_QS);
      // Reload dashboard so cold-start banner reflects new overhead
      await loadDashboardData();
    } catch (err) {
      setQsError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setQsSubmitting(false);
    }
  };

  const handleQsClose = () => {
    if (qsSubmitting) return;
    setQsOpen(false);
    setQsError(null);
    setQsSuccess(false);
    setQsValues(EMPTY_QS);
  };

  // ── Annual preview ──────────────────────────────────────────────────────────
  const qsTotal = ['rent', 'utilities', 'insurance', 'other'].reduce((sum, k) => {
    return sum + (parseFloat(qsValues[k]) || 0);
  }, 0);
  const qsAnnual = qsTotal * 12;

  // ── Quick actions ───────────────────────────────────────────────────────────
  const quickActions = [
    {
      title: 'Business Settings',
      description: 'Configure profit targets, trading hours, and VAT',
      icon: <SettingsIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      action: () => navigate(location.pathname.startsWith('/solo') ? '/solo/calculator/settings' : '/calculator/settings')
    },
    {
      title: 'Manage Services',
      description: 'Add, edit, and price your salon services',
      icon: <EditIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      action: () => navigate(location.pathname.startsWith('/solo') ? '/solo/calculator/services' : '/calculator/services')
    },
    {
      title: 'Calculate Overheads',
      description: 'Update overhead costs from your expenses',
      icon: <CalculateIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      action: () => navigate(location.pathname.startsWith('/solo') ? '/solo/calculator/overheads' : '/calculator/overheads')
    }
  ];

  const statCards = [
    {
      title: 'Total Services',
      value: stats.totalServices,
      icon: <EditIcon />,
      color: '#2196F3',
      bgColor: 'rgba(33, 150, 243, 0.1)'
    },
    {
      title: 'Categories',
      value: stats.totalCategories,
      icon: <CategoryIcon />,
      color: '#FF9800',
      bgColor: 'rgba(255, 152, 0, 0.1)'
    },
    {
      title: 'Average Tier Price',
      value: `£${stats.averagePrice.toFixed(2)}`,
      icon: <MoneyIcon />,
      color: '#9C27B0',
      bgColor: 'rgba(156, 39, 176, 0.1)'
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            p: 4,
            mb: 3,
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
            <CalculateIcon sx={{ fontSize: 48, color: '#2196F3' }} />
            <Typography variant="h3" sx={{ fontWeight: 300, color: '#333' }}>
              Pricing Calculator
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Professional service pricing based on your actual business costs
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Cold-start warning — disappears once overhead_per_minute is configured */}
        {isColdStart && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<QuickStartIcon />}
                onClick={() => setQsOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Quick Start
              </Button>
            }
          >
            <AlertTitle>Calculator not set up yet</AlertTitle>
            Your overhead costs haven't been entered — all recommended prices will show as £0.00 until
            you add your costs. Click <strong>Quick Start</strong> to enter estimated monthly costs
            and get started immediately.
          </Alert>
        )}

        {/* Statistics Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        backgroundColor: stat.bgColor,
                        color: stat.color,
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: stat.color, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Business Foundation */}
        {businessSettings && (
          <Paper elevation={0} sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Business Foundation
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Monthly Profit Target</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    £{parseFloat(businessSettings.monthly_profit_target || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Weekly Trading Hours</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {parseFloat(businessSettings.total_working_hours || 0).toFixed(1)}h
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Overhead Per Minute</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    £{parseFloat(businessSettings.overhead_per_minute || 0).toFixed(4)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Profit Per Minute</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    £{parseFloat(businessSettings.owner_profit_per_minute || 0).toFixed(4)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Quick Actions */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={3}>
          {quickActions.map((action, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    borderColor: action.color
                  }
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ color: action.color, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: action.color,
                      '&:hover': { backgroundColor: action.color, filter: 'brightness(0.9)' }
                    }}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Quick Start Dialog ──────────────────────────────────────────────── */}
      <Dialog open={qsOpen} onClose={handleQsClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuickStartIcon color="warning" />
            Quick Start — Estimated Overheads
          </Box>
        </DialogTitle>

        <DialogContent>
          {qsSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              <AlertTitle>Done!</AlertTitle>
              Your estimated overhead has been saved and the calculator has been updated.
              Remember to replace these figures with your actual expenses as you go —
              they appear in Expenditure under the <strong>QS *</strong> categories and
              can be deleted once you have real data.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
                Enter your estimated <strong>monthly</strong> fixed costs. These are saved as
                annual figures so the calculator can produce recommended prices straight away.
                <br /><br />
                <strong>Do not include staff wages or product costs</strong> — those are handled
                separately through tier hourly rates and per-service product costing.
                <br /><br />
                These estimates appear in Expenditure under <strong>QS Rent</strong>,{' '}
                <strong>QS Utilities</strong> etc. and can be deleted once you have real
                expense records entered.
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Rent"
                    type="number"
                    value={qsValues.rent}
                    onChange={e => setQsValues(v => ({ ...v, rent: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    helperText="Monthly rent or mortgage payment"
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Utilities"
                    type="number"
                    value={qsValues.utilities}
                    onChange={e => setQsValues(v => ({ ...v, utilities: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    helperText="Gas, electric, water combined"
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Insurance"
                    type="number"
                    value={qsValues.insurance}
                    onChange={e => setQsValues(v => ({ ...v, insurance: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    helperText="Public liability, professional indemnity etc."
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other fixed costs"
                    type="number"
                    value={qsValues.other}
                    onChange={e => setQsValues(v => ({ ...v, other: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    helperText="Subscriptions, rates, comms, etc."
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
              </Grid>

              {qsTotal > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Monthly total
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      £{qsTotal.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Annual total (saved to calculator)
                    </Typography>
                    <Typography variant="body1" fontWeight={700} color="primary.main">
                      £{qsAnnual.toFixed(2)}
                    </Typography>
                  </Box>
                </>
              )}

              {qsError && (
                <Alert severity="error" sx={{ mt: 2 }} style={{ whiteSpace: 'pre-line' }}>
                  {qsError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleQsClose} disabled={qsSubmitting}>
            {qsSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!qsSuccess && (
            <Button
              variant="contained"
              onClick={handleQsSubmit}
              disabled={qsSubmitting || qsTotal === 0}
              startIcon={qsSubmitting ? <CircularProgress size={16} /> : <QuickStartIcon />}
            >
              {qsSubmitting ? 'Saving...' : 'Save & Calculate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalculatorDashboard;